import phoneUI from 'platform/forms-system/src/js/definitions/phone';
import emailUI from 'platform/forms-system/src/js/definitions/email';
import AddressViewField from 'platform/forms-system/src/js/components/AddressViewField';
import VaSelectField from 'platform/forms-system/src/js/web-component-fields/VaSelectField';

import ReviewCardField from 'platform/forms-system/src/js/components/ReviewCardField';

import {
  addressUI,
  descriptionUI,
  titleUI,
  updateFormDataAddress,
} from 'platform/forms-system/src/js/web-component-patterns';

import vetsJsonSchema from 'vets-json-schema/dist/21-526EZ-ALLCLAIMS-V2-schema.json';

import {
  contactInfoDescription,
  contactInfoTitle,
  contactInfoUpdateHelpDescription,
} from './contactInformationContent';
import { phoneEmailViewField } from '../../../shared/components/phoneEmailViewField';
import { validateZIP } from '../../../shared/utils/validateZIP';
import ZipCodeInput from '../../../shared/components/ZipCodeInput';
import { mailingAddressSchema } from '../../utils/contactInformationSchema';
import {
  shouldAutoDetectMilitary,
  shouldShowZipCode,
  createAddressValidator,
  hasInvalidPrefillData,
  updateCountrySchema,
  updateStateSchema,
  isStateRequired,
  isCountryRequired,
  shouldHideState,
} from '../../../shared/utils/contactInformationHelpers';

// The published schema has no `phoneAndEmail`; the card reuses the field definitions of the
// `veteranIdentification` it is submitted as.
const { emailAddress, veteranPhoneNumber } =
  vetsJsonSchema.properties.veteranIdentification.properties;

const phoneAndEmail = {
  type: 'object',
  required: ['emailAddress'],
  properties: {
    primaryPhone: veteranPhoneNumber.properties.telephone,
    emailAddress,
  },
};

const defaultAddressUI = {
  ...addressUI({
    keys: {
      street: 'addressLine1',
      street2: 'addressLine2',
      street3: 'addressLine3',
      postalCode: 'zipCode',
      isMilitary: 'view:livesOnMilitaryBase',
    },
    validations: {
      street: [createAddressValidator('addressLine1')],
      street2: [createAddressValidator('addressLine2')],
      street3: [createAddressValidator('addressLine3')],
      city: [createAddressValidator('city')],
    },
  }),
};

const updateFormData = (oldFormData, formData) => {
  const updatedFormData = updateFormDataAddress(
    oldFormData,
    formData,
    ['mailingAddress'],
    null,
    {
      street: 'addressLine1',
      street2: 'addressLine2',
      street3: 'addressLine3',
      postalCode: 'zipCode',
      isMilitary: 'view:livesOnMilitaryBase',
    },
  );

  if (shouldAutoDetectMilitary(oldFormData, updatedFormData)) {
    updatedFormData.mailingAddress['view:livesOnMilitaryBase'] = true;
  }

  return updatedFormData;
};

const uiSchema = {
  ...titleUI(contactInfoTitle),
  ...descriptionUI(contactInfoDescription),
  phoneAndEmail: {
    'ui:title': 'Phone & email',
    'ui:field': ReviewCardField,
    'ui:options': {
      viewComponent: phoneEmailViewField,
    },
    primaryPhone: phoneUI('Phone number'),
    emailAddress: emailUI(),
  },
  mailingAddress: {
    ...defaultAddressUI,
    'ui:title': 'Mailing address',
    'ui:field': ReviewCardField,
    'ui:options': {
      ...defaultAddressUI['ui:options'],
      viewComponent: AddressViewField,
      classNames:
        'vads-web-component-pattern vads-web-component-pattern-address',
      startInEdit: addressData =>
        hasInvalidPrefillData(addressData) ||
        (!addressData?.country && !addressData?.['view:livesOnMilitaryBase']),
    },
    country: {
      ...defaultAddressUI.country,
      'ui:required': isCountryRequired,
      'ui:options': {
        ...defaultAddressUI.country['ui:options'],
        updateSchema: updateCountrySchema,
      },
    },
    state: {
      'ui:title': 'State',
      'ui:autocomplete': 'address-level1',
      'ui:webComponentField': VaSelectField,
      'ui:errorMessages': {
        required: 'Please select a state',
      },
      'ui:options': {
        hideIf: shouldHideState,
        classNames:
          'vads-web-component-pattern-field vads-web-component-pattern-address',
        hideEmptyValueInReview: true,
        updateSchema: updateStateSchema,
      },
      'ui:required': isStateRequired,
    },
    zipCode: {
      ...defaultAddressUI.zipCode,
      'ui:webComponentField': ZipCodeInput,
      'ui:required': shouldShowZipCode,
      'ui:validations': [validateZIP],
      'ui:errorMessages': {
        required: 'Please enter a postal code',
        pattern:
          'Please enter a valid 5- or 9-digit postal code (dashes allowed)',
      },
      'ui:options': {
        classNames:
          'vads-web-component-pattern-field vads-web-component-pattern-address',
        widgetClassNames: 'usa-input-medium',
        hideIf: formData => !shouldShowZipCode(formData),
      },
    },
    addressLine1: {
      ...defaultAddressUI.addressLine1,
      'ui:options': {
        ...defaultAddressUI.addressLine1['ui:options'],
        charcount: true,
      },
    },
    addressLine2: {
      ...defaultAddressUI.addressLine2,
      'ui:options': {
        ...defaultAddressUI.addressLine2['ui:options'],
        charcount: true,
      },
    },
    addressLine3: {
      ...defaultAddressUI.addressLine3,
      'ui:options': {
        ...defaultAddressUI.addressLine3['ui:options'],
        charcount: true,
      },
    },
    city: {
      ...defaultAddressUI.city,
      'ui:options': {
        ...defaultAddressUI.city['ui:options'],
        charcount: true,
      },
    },
  },
  'view:contactInfoDescription': {
    'ui:description': contactInfoUpdateHelpDescription,
  },
};

export default {
  uiSchema,
  schema: {
    type: 'object',
    properties: {
      phoneAndEmail,
      mailingAddress: mailingAddressSchema,
      'view:contactInfoDescription': {
        type: 'object',
        properties: {},
      },
    },
  },
  updateFormData,
};
